import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, PartyPopper, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { PlayIntegrityService } from '@/services/PlayIntegrityService';
import { invokeAuthedEdge, InvokeNotAuthenticatedError } from '@/lib/supabase/invokeAuthedEdge';
import { toast } from 'sonner';
import { useUpdateCurrentUser } from '@/contexts/NetworkDataContext';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent, AnalyticsEvents } from '@/analytics';

/**
 * V210.9: Purchase Success Handler
 * * Prevents race condition between app reload and webhook arrival by:
 * 1. Catching purchase success event
 * 2. Calling google-verify-purchase immediately (manual sync)
 * 3. Showing success dialog
 * 4. Reloading app only after DB is updated
 */

// Best Practice: Type the global window object instead of casting to 'any'
declare global {
  interface Window {
    __v210_9_purchase_handled?: boolean;
  }
}

type PurchaseStatus = 'idle' | 'syncing' | 'success' | 'error';

interface PurchaseDetail {
  purchaseToken: string;
  productId?: string;
  packageName?: string;
}

export const PurchaseSuccessHandler: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<PurchaseStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { user } = useAuth();
  const updateCurrentUser = useUpdateCurrentUser();

  useEffect(() => {
    const handlePurchase = async (event: CustomEvent<PurchaseDetail>) => {
      const { purchaseToken, productId, packageName } = event.detail || {};

      if (!purchaseToken) {
        console.warn('💰 V210.9: Purchase event missing purchaseToken, ignoring');
        return;
      }

      console.log('💰 V210.9: Purchase detected! Starting manual sync...', {
        productId,
        packageName,
        tokenPreview: purchaseToken.substring(0, 20) + '...'
      });

      // V210.9: Set flag to prevent VerifyPaywall polling
      window.__v210_9_purchase_handled = true;

      setIsOpen(true);
      setStatus('syncing');
      setErrorMessage('');

      try {
        // V316.4 TASK B: Purchase flow proof - log before integrity call
        console.log('[Purchase] starting purchase flow - integrity purpose=purchase');

        // V315.9 TASK C: Purchase integrity gate – ALWAYS call run(); let it decide and log why it skipped
        let integrityCheckId: string | null = null;
        // V316.6: Added client_source tag to prove call-site
        const integrityResult = await PlayIntegrityService.run('purchase', 'purchase_success');

        // V316.4 TASK B: Purchase flow proof - log after integrity result
        const integrityOk = integrityResult?.ok ?? false;
        const integrityReason = integrityResult?.reason ?? 'null_result';
        const integrityCheckIdLast6 = integrityResult?.check_id && integrityResult.check_id.length >= 6
          ? integrityResult.check_id.slice(-6)
          : integrityResult?.check_id ?? 'null';
        console.log(`[Purchase] integrity result ok=${integrityOk} reason=${integrityReason} check_id=${integrityCheckIdLast6}`);

        if (integrityResult === null) {
          // V315.9 TASK C: PI returned null (skipped) - log and block purchase
          console.log('[Purchase] PI returned null (skipped) — cannot proceed (see PlayIntegrity logs for reason)');
          setStatus('error');
          setErrorMessage('Device verification required. Please try again or contact support.');
          toast.error('Verification required', { description: 'Device check skipped. Please try again.' });
          return;
        }

        if (integrityResult.ok !== true) {
          console.warn('💰 V315.9: Purchase integrity failed, not granting Verified', integrityResult);
          setStatus('error');
          setErrorMessage('Device verification required. Please try again or contact support.');
          toast.error('Verification required', { description: 'Device check failed. Please try again.' });
          return;
        }

        integrityCheckId = integrityResult.check_id ?? null;

        console.log('🔄 V210.9: Calling google-verify-purchase edge function via invokeAuthedEdge...');

        let data: { verified?: boolean; userId?: string; trustScoreDelta?: number } | null = null;
        let error: { code?: string; message?: string; requestId?: string } | null = null;
        let requestId: string | undefined;
        try {
          const result = await invokeAuthedEdge<{ verified?: boolean; userId?: string; trustScoreDelta?: number }>(
            'google-verify-purchase',
            {
              purchaseToken,
              productId: productId || 'verified_monthly',
              packageName: packageName || 'com.blissnet.app',
              platform: 'android',
              ...(integrityCheckId != null && { integrityCheckId }),
            },
            { route: 'purchase_success_handler' }
          );
          data = result.data;
          error = result.error;
          requestId = result.requestId;
        } catch (err) {
          if (err instanceof InvokeNotAuthenticatedError) {
            console.error('[Purchase] google-verify-purchase not authenticated requestId=%s', err.requestId);
            toast.error('Session expired. Please sign in again and retry.', {
              description: err.requestId ? `Request ID: ${err.requestId}` : undefined,
              duration: 4000,
            });
            setStatus('success');
            setErrorMessage('Session expired. Please sign in again if verification does not appear shortly.');
            return;
          }
          throw err;
        }

        if (error || (data && (data as { ok?: boolean }).ok === false)) {
          const msg = error?.message ?? 'Verification failed';
          const rid = error?.requestId ?? requestId;
          console.error('[Purchase] google-verify-purchase failed', { error, data, requestId: rid });
          toast.error(msg, { description: rid ? `Request ID: ${rid}` : undefined, duration: 5000 });
          setStatus('error');
          setErrorMessage(msg);
          return;
        }

        if (!data?.verified) {
          const msg = (data as { error?: string })?.error ?? 'Purchase not verified';
          console.error('[Purchase] google-verify-purchase not verified', { data, requestId });
          toast.error(msg, { description: requestId ? `Request ID: ${requestId}` : undefined, duration: 5000 });
          setStatus('error');
          setErrorMessage(msg);
          return;
        }

        console.log('[Purchase] google-verify-purchase succeeded requestId=%s', requestId);

        trackEvent(AnalyticsEvents.PURCHASE_SUCCEEDED).catch(() => {});

        if (user?.id && updateCurrentUser) {
          const { data: freshProfile } = await supabase
            .from('users')
            .select('is_verified, trust_score')
            .eq('id', user.id)
            .single();
          if (freshProfile) {
            await updateCurrentUser({ is_verified: freshProfile.is_verified ?? false, trust_score: freshProfile.trust_score ?? 0 });
          }
        }

        await new Promise(resolve => setTimeout(resolve, 500));
        setStatus('success');

      } catch (err: unknown) {
        const ex = err as { message?: string; requestId?: string };
        const msg = ex?.message ?? 'Verification failed';
        const rid = ex?.requestId;
        console.error('[Purchase] google-verify-purchase exception', { err, requestId: rid });
        toast.error(msg, { description: rid ? `Request ID: ${rid}` : undefined, duration: 5000 });
        setStatus('success');
        setErrorMessage('Verification in progress. This may take a moment.');
      }
    };

    // V210.9: Listen for custom purchase success event
    window.addEventListener('blissnet_purchase_success', handlePurchase as EventListener);

    return () => {
      window.removeEventListener('blissnet_purchase_success', handlePurchase as EventListener);
    };
  }, [user, updateCurrentUser]);

  const handleClose = () => {
    console.log('🔄 V210.9: User clicked "Start Exploring", reloading app...');
    setIsOpen(false);

    // V210.9: Reload to refresh verification state safely
    // At this point, DB is updated, so google-check-status will pass
    window.location.reload();
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {/* Prevent closing by clicking outside via state control */ }}>
      <DialogContent
        className="sm:max-w-md bg-zinc-950 border-purple-500/30 text-white"
        // Best Practice: Lock modal interactions during critical sync
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >

        {status === 'syncing' && (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
            <h2 className="text-lg font-semibold">Activating Subscription...</h2>
            <p className="text-zinc-400 text-sm text-center">
              Syncing with Google Play
            </p>
          </div>
        )}

        {status === 'success' && (
          <>
            <DialogHeader>
              <div className="mx-auto bg-green-500/20 p-3 rounded-full mb-4">
                <PartyPopper className="w-10 h-10 text-green-400" />
              </div>
              <DialogTitle className="text-center text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600">
                You're Verified! +100 Trust Score
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col items-center space-y-4 text-center">
              <p className="text-zinc-300 text-sm">
                Your verified status and +100 trust score boost are now active on your profile.
              </p>

              {errorMessage && (
                <div className="w-full bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-yellow-200">{errorMessage}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 w-full mt-2">
                <div className="bg-zinc-900 border border-zinc-800 p-3 rounded-lg flex flex-col items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-400 mb-1" />
                  <span className="text-xs font-medium">Verified Badge</span>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 p-3 rounded-lg flex flex-col items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-purple-400 mb-1" />
                  <span className="text-xs font-medium">+100 Trust Score</span>
                </div>
              </div>

              <Button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-6 mt-2"
              >
                Start Exploring
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};